/**
 * Returns a period label in months for a given number of months, using i18n translations.
 */
export function getPeriodLabel(
  months: number,
  t: Record<string, string>,
): string {
  if (months === 1) {
    return `1 ${t.month_singular}`;
  }
  return `${months} ${t.months}`;
}
/**
 * Personal Loan Calculator Utilities
 * Implements EMI, EIR (using IRR), and amortization schedule calculations
 */

export interface LoanInput {
  loanAmount: number;
  processingFee: number;
  annualInterestRate: number; // percentage (e.g., 5.5 for 5.5%)
  repaymentPeriodMonths: number;
  startDate: Date;
}

export interface PaymentScheduleItem {
  paymentNumber: number;
  paymentDate: Date;
  principal: number;
  interest: number;
  totalPayment: number;
  remainingPrincipal: number;
}

export interface LoanCalculationResult {
  monthlyRepayment: number;
  totalRepayment: number;
  totalInterest: number;
  /**
   * EIR calculated using IRR
   */
  effectiveInterestRate: number;
  paymentSchedule: PaymentScheduleItem[];
  processingFee: number;
  annualInterestRate: number;
}

/**
 * Calculate EMI (Equated Monthly Installment)
 * Formula: EMI = (P + F) * R * (1 + R)^N / ((1 + R)^N - 1)
 * Where:
 *   P = Loan Amount
 *   F = Processing Fee
 *   R = Monthly Interest Rate (Annual Rate / 12 / 100)
 *   N = Number of months
 */
export function calculateEMI(
  principal: number,
  processingFee: number,
  annualRate: number,
  months: number,
): number {
  if (months === 0) {
    return 0;
  }

  if (annualRate === 0) {
    return (principal + processingFee) / months;
  }

  const monthlyRate = annualRate / 12 / 100;
  const totalAmount = principal + processingFee;

  const numerator =
    totalAmount * monthlyRate * Math.pow(1 + monthlyRate, months);
  const denominator = Math.pow(1 + monthlyRate, months) - 1;

  return numerator / denominator;
}

/**
 * Generate amortization schedule
 */
export function generatePaymentSchedule(
  loanAmount: number,
  processingFee: number,
  annualRate: number,
  months: number,
  startDate: Date,
  emi: number,
): PaymentScheduleItem[] {
  const schedule: PaymentScheduleItem[] = [];
  const monthlyRate = annualRate / 12 / 100;
  let remainingPrincipal = loanAmount + processingFee;

  for (let i = 1; i <= months; i++) {
    const interestPayment = remainingPrincipal * monthlyRate;
    const principalPayment = emi - interestPayment;
    remainingPrincipal -= principalPayment;

    // Calculate payment date
    const paymentDate = new Date(startDate);
    paymentDate.setMonth(paymentDate.getMonth() + i);

    schedule.push({
      paymentNumber: i,
      paymentDate,
      principal: principalPayment,
      interest: interestPayment,
      totalPayment: emi,
      remainingPrincipal: Math.max(0, remainingPrincipal), // Avoid negative due to rounding
    });
  }

  return schedule;
}

/**
 * Calculate IRR (Internal Rate of Return) to derive EIR
 * Uses Newton-Raphson method for iterative approximation
 */
function calculateIRR(cashflows: number[], guess: number = 0.1): number {
  const maxIterations = 100;
  const tolerance = 0.0001;
  let rate = guess;

  for (let i = 0; i < maxIterations; i++) {
    let npv = 0;
    let dnpv = 0;

    for (let t = 0; t < cashflows.length; t++) {
      const discountFactor = Math.pow(1 + rate, t);
      npv += cashflows[t] / discountFactor;
      dnpv -= (t * cashflows[t]) / (discountFactor * (1 + rate));
    }

    if (Math.abs(dnpv) < tolerance) {
      return rate;
    }

    const newRate = rate - npv / dnpv;

    if (Math.abs(newRate - rate) < tolerance) {
      return newRate;
    }

    rate = newRate;
  }

  return rate;
}

/**
 * Calculate Effective Interest Rate (EIR) using IRR
 * EIR accounts for processing fees and represents the true cost of borrowing
 */
export function calculateEIR(
  loanAmount: number,
  processingFee: number,
  emi: number,
  months: number,
): number {
  // Build cashflow array
  // t=0: Actual amount received (loan amount - processing fee flows in)
  // t=1 to N: Monthly payments (EMI flows out)
  const cashflows: number[] = [loanAmount - processingFee]; // Initial cashflow

  for (let i = 0; i < months; i++) {
    cashflows.push(-emi); // Negative because it's an outflow
  }

  // Calculate monthly IRR
  const monthlyIRR = calculateIRR(cashflows, 0.01);

  // Convert monthly IRR to annual EIR
  const annualEIR = (Math.pow(1 + monthlyIRR, 12) - 1) * 100;

  return annualEIR;
}

/**
 * Main calculation function that combines all calculations
 */
export function calculateLoan(input: LoanInput): LoanCalculationResult {
  const {
    loanAmount,
    processingFee,
    annualInterestRate,
    repaymentPeriodMonths,
    startDate,
  } = input;

  // Calculate EMI
  const emi = calculateEMI(
    loanAmount,
    processingFee,
    annualInterestRate,
    repaymentPeriodMonths,
  );

  // Generate payment schedule
  const paymentSchedule = generatePaymentSchedule(
    loanAmount,
    processingFee,
    annualInterestRate,
    repaymentPeriodMonths,
    startDate,
    emi,
  );

  // Calculate totals
  const totalRepayment = emi * repaymentPeriodMonths;
  const totalInterest = totalRepayment - (loanAmount + processingFee);

  // Calculate EIR
  const eir = calculateEIR(
    loanAmount,
    processingFee,
    emi,
    repaymentPeriodMonths,
  );

  return {
    monthlyRepayment: emi,
    totalRepayment,
    totalInterest,
    effectiveInterestRate: eir,
    paymentSchedule,
    processingFee,
    annualInterestRate,
  };
}

/**
 * Format currency with thousand separators
 */
export function formatCurrency(
  amount: number,
  currencySymbol: string = '$',
  decimals: number = 2,
): string {
  const formattedAmount = amount
    .toFixed(decimals)
    .replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return `${currencySymbol}${formattedAmount}`;
}

/**
 * Format date for display
 */
export function formatDate(date: Date): string {
  const day = date.getDate().toString().padStart(2, '0');
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
}

// Format month label for schedule table
export const formatMonthLabel = (paymentDate: Date, lang: string): string => {
  // e.g., 'Aug 2025'
  const month = paymentDate.toLocaleString(lang, { month: 'short' });
  const year = paymentDate.getFullYear();
  return `${month} ${year}`;
};

// Format number input with thousand separators
export const formatNumberInput = (value: string, decimals: number): string => {
  const num = value.replace(/[^\d.]/g, '');
  if (!num) {
    return '';
  }

  return formatCurrency(Number(num), '', decimals);
};

// Parse formatted number back to number
export const parseFormattedNumber = (value: string): number => {
  return Number(value.replace(/,/g, '')) || 0;
};

/**
 * Count how many digit characters appear before the given cursor position.
 */
export function countDigitsBefore(value: string, cursorPos: number): number {
  let count = 0;
  for (let i = 0; i < cursorPos && i < value.length; i++) {
    if (/\d/.test(value[i])) {
      count++;
    }
  }
  return count;
}

/**
 * Find the cursor position in a formatted string such that `digitsBefore` digit
 * characters precede it. Returns the position right after the last matched digit,
 * or the end of the string if there aren't enough digits.
 */
export function findCursorPosition(
  formatted: string,
  digitsBefore: number,
): number {
  if (digitsBefore === 0) {
    return 0;
  }
  let count = 0;
  for (let i = 0; i < formatted.length; i++) {
    if (/\d/.test(formatted[i])) {
      count++;
    }
    if (count === digitsBefore) {
      return i + 1;
    }
  }
  return formatted.length;
}

/**
 * Format a raw input value with thousand-separator commas for real-time display.
 * Unlike `formatCurrency`, this does NOT apply `toFixed` padding — it preserves
 * the decimal part exactly as the user typed it (including trailing dots).
 */
export function formatInputValue(value: string, decimals: number): string {
  if (decimals === 0) {
    // Integer mode: strip everything except digits
    const digits = value.replace(/\D/g, '');
    if (!digits) return '';
    // Strip leading zeros
    const cleaned = digits.replace(/^0+(?=\d)/, '');
    return cleaned.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  // Decimal mode: strip everything except digits and dots
  let stripped = value.replace(/[^\d.]/g, '');
  // Keep only the first decimal point
  const dotIndex = stripped.indexOf('.');
  if (dotIndex !== -1) {
    const beforeDot = stripped.substring(0, dotIndex);
    const afterDot = stripped.substring(dotIndex + 1).replace(/\./g, '');
    stripped = `${beforeDot}.${afterDot}`;
  }
  if (!stripped) return '';

  const parts = stripped.split('.');
  // Strip leading zeros from integer part
  let intPart = parts[0].replace(/^0+(?=\d)/, '') || '0';
  // Add thousand separators to integer part
  intPart = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');

  if (parts.length === 1) {
    return intPart;
  }
  // Preserve decimal part as-is (no toFixed padding)
  return `${intPart}.${parts[1]}`;
}
